import { useEffect, useState } from "react";
import { PenLine, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString() : null);

export function PspConsentPanel({ driver }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/drivers/${driver.id}/psp-consent`).then(({ data }) => setRec(data)).catch(() => setRec({}));
  useEffect(() => { load(); }, [driver.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { await api.post(`/drivers/${driver.id}/psp-consent/send`); toast.success("PSP authorization sent for e-signature"); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const manual = async (v) => {
    try { const { data } = await api.patch(`/drivers/${driver.id}/psp-consent`, { signed_at: v || null }); setRec(data); toast.success(v ? "PSP authorization date saved" : "Cleared"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!rec) return null;
  const signed = rec.signed_at;
  const pending = rec.signature_id && !signed;
  return (
    <div className={`rounded-md border p-3 text-sm ${signed ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid="psp-consent-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`h-4 w-4 ${signed ? "text-emerald-700" : "text-amber-700"}`} />
          <span className="tag-mono">PSP DISCLOSURE &amp; AUTHORIZATION</span>
          <span className="text-xs text-slate-600" data-testid="psp-consent-status">{signed ? `Signed ${fmt(signed)}${rec.method === "e-signature" ? " · e-signature" : ""}` : pending ? `Sent ${fmt((rec.requested_at || "").slice(0, 10))} — awaiting driver signature` : "Not on file — required before ordering a PSP report"}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {signed && rec.signature_id && <a className="inline-flex items-center gap-1 text-blue-700 hover:underline" href={`${API}/signatures/${rec.signature_id}/file?signed=true`} target="_blank" rel="noreferrer" data-testid="psp-consent-view"><FileText className="h-3.5 w-3.5" />View signed (PDF)</a>}
          <button type="button" disabled={busy} onClick={send} className="inline-flex items-center gap-1 text-blue-700 hover:underline disabled:opacity-50" data-testid="psp-consent-send"><PenLine className="h-3.5 w-3.5" />{pending ? "Resend for e-signature" : signed ? "Send a new authorization" : "Send for e-signature"}</button>
        </div>
      </div>
      {!signed && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <span>Signed on paper? Enter the date:</span>
          <Input type="date" className="h-7 w-40" defaultValue="" onChange={(e) => e.target.value && manual(e.target.value)} data-testid="psp-consent-manual-date" />
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">FCRA stand-alone disclosure plus the FMCSA PSP authorization (5-year crash / 3-year inspection history). The signed copy is stored on the driver and appears in the DQ binder under Signed policies &amp; acknowledgments.</p>
    </div>
  );
}
